import React from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { Mail, Download, ChevronUp, ChevronDown, Edit2, Save, X } from 'lucide-react';
import { __ } from './translations';

const AdminHistory = () => {
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [sorting, setSorting] = React.useState([]);
    const [columnFilters, setColumnFilters] = React.useState([]);
    const [editingRow, setEditingRow] = React.useState(null);
    const [editValue, setEditValue] = React.useState("");

    React.useEffect(() => {
        fetchAllHistory();
    }, []);

    const fetchAllHistory = async () => {
        try {
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/all-contributions-history`, {
                headers: {
                    'X-WP-Nonce': boardMemberData.nonce,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch history');
            }

            const fetchedData = await response.json();
            setData(fetchedData);
        } catch (err) {
            console.error('Error fetching history:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditSave = async (historyId) => {
        try {
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/update-contribution`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce,
                },
                body: JSON.stringify({
                    history_id: historyId,
                    income: parseFloat(editValue)
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update contribution');
            }

            const updatedData = await response.json();
            
            // Update local data state
            setData(prevData => prevData.map(item => 
                item.id === historyId 
                    ? { ...item, income: editValue, contribution: updatedData.contribution }
                    : item
            ));

            setEditingRow(null);
            setEditValue("");
            alert(__('Contribution updated successfully.'));
            
            // Refresh the data to get the new calculations
            fetchAllHistory();
            
        } catch (error) {
            console.error('Error updating contribution:', error);
            alert(__('Failed to update contribution.'));
        }
    };

    const handleRegenerateEmail = async (historyId) => {
        try {
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/regenerate-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce,
                },
                body: JSON.stringify({ history_id: historyId }),
            });
            if (response.ok) {
                alert(__('Email has been regenerated and sent.'));
            } else {
                throw new Error('Failed to regenerate email');
            }
        } catch (error) {
            console.error('Error regenerating email:', error);
            alert(__('Failed to regenerate email.'));
        }
    };

    const handleGenerateOldPdf = async (historyId) => {
        try {
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/generate-old-pdf`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce,
                },
                body: JSON.stringify({ history_id: historyId }),
            });
            const data = await response.json();

            if (data.pdf_link) {
                alert(__('PDF has been regenerated.'));
                window.open(data.pdf_link, '_blank');
            } else {
                alert(__('Failed to generate PDF.'));
            }
        } catch (error) {
            console.error(__('Error generating PDF:'), error);
        }
    };

    const columns = React.useMemo(
        () => [
            {
                accessorKey: 'company_name',
                header: __('Company'),
                cell: info => info.getValue(),
            },
            {
                accessorKey: 'user_name',
                header: __('User'),
                cell: info => info.getValue(),
            },
            {
                accessorKey: 'year',
                header: __('Year'),
                cell: info => info.getValue(),
            },
            {
                accessorKey: 'income',
                header: __('Income'),
                cell: info => {
                    const rowId = info.row.original.id;
                    const isSupporter = info.row.original.member_type === 'supporter';

                    // For supporters, just display the value without edit option
                    if (isSupporter) {
                        return (
                            <div className="flex items-center">
                                <span>{info.getValue() ? `${info.getValue()} MLN PLN` : '—'}</span>
                                <span className="ml-2 text-xs text-gray-500 italic">
                                    ({__('Supporter - Fixed Rate')})
                                </span>
                            </div>
                        );
                    }

                    // For regular members, show edit functionality
                    if (editingRow === rowId) {
                        return (
                            <div className="flex items-center space-x-2">
                                <input
                                    type="number"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="w-24 p-1 border rounded"
                                    step="0.01"
                                />
                                <button
                                    onClick={() => handleEditSave(rowId)}
                                    className="text-green-600 hover:text-green-900"
                                >
                                    <Save className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingRow(null);
                                        setEditValue("");
                                    }}
                                    className="text-red-600 hover:text-red-900"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        );
                    }
                    
                    return (
                        <div className="flex items-center space-x-2">
                            <span>{`${info.getValue()} MLN PLN`}</span>
                            <button
                                onClick={() => {
                                    setEditingRow(rowId);
                                    setEditValue(info.getValue());
                                }}
                                className="text-blue-600 hover:text-blue-900"
                            >
                                <Edit2 className="h-4 w-4" />
                            </button>
                        </div>
                    );
                },
            },
            {
                accessorKey: 'contribution',
                header: __('Contribution'),
                cell: info => `${info.getValue()} PLN`,
            },
            {
                accessorKey: 'date',
                header: __('Date'),
                cell: info => info.getValue(),
            },
            {
                id: 'actions',
                header: __('Actions'),
                cell: info => (
                    <div className="flex space-x-2">
                        <button
                            onClick={() => handleRegenerateEmail(info.row.original.id)}
                            className="text-blue-600 hover:text-blue-900 p-1 transition-colors"
                            title={__('Regenerate Email')}
                        >
                            <Mail className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => handleGenerateOldPdf(info.row.original.id)}
                            className="text-blue-600 hover:text-blue-900 p-1 transition-colors"
                            title={__('Generate PDF')}
                        >
                            <Download className="h-5 w-5" />
                        </button>
                    </div>
                ),
            },
        ],
        [editingRow, editValue]
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center p-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="space-y-4">
            <div className="rounded-md border">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th key={header.id} className="px-6 py-3 bg-gray-50">
                                        {header.isPlaceholder ? null : (
                                            <div>
                                                <div
                                                    className="flex items-center justify-between cursor-pointer"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    <span className="text-xs font-medium text-gray-500 uppercase">
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </span>
                                                    {header.column.getCanSort() && (
                                                        <div className="ml-2">
                                                            {{
                                                                asc: <ChevronUp className="h-4 w-4" />,
                                                                desc: <ChevronDown className="h-4 w-4" />,
                                                            }[header.column.getIsSorted()] ?? (
                                                                    <ChevronDown className="h-4 w-4 opacity-30" />
                                                                )}
                                                        </div>
                                                    )}
                                                </div>
                                                {header.column.getCanFilter() && header.column.id !== 'actions' && (
                                                    <div className="mt-2">
                                                        <input
                                                            type="text"
                                                            value={header.column.getFilterValue() ?? ''}
                                                            onChange={e => header.column.setFilterValue(e.target.value)}
                                                            placeholder={`Search ${header.column.columnDef.header}...`}
                                                            className="w-full p-2 text-xs border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id} className="hover:bg-gray-50">
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-2">
                    <button
                        className="border rounded p-1"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {'<<'}
                    </button>
                    <button
                        className="border rounded p-1"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {'<'}
                    </button>
                    <button
                        className="border rounded p-1"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        {'>'}
                    </button>
                    <button
                        className="border rounded p-1"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        {'>>'}
                    </button>
                    <span className="flex items-center gap-1">
                        <div>Page</div>
                        <strong>
                            {table.getState().pagination.pageIndex + 1} of{' '}
                            {table.getPageCount()}
                        </strong>
                    </span>
                </div>
                <select
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                        table.setPageSize(Number(e.target.value));
                    }}
                    className="border rounded p-1"
                >
                    {[10, 20, 30, 40, 50].map(pageSize => (
                        <option key={pageSize} value={pageSize}>
                            Show {pageSize}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default AdminHistory;